<template>
  <page :head-text="$t('login.cloudzet_welcome')">
    <template #leftBtn>
      <div @click="launchQrScan" style="margin: -1rem; padding: 1rem;">
        <img :src="brandAsset(icons.scan, icons.scanSanne)" alt="scan" />
      </div>
    </template>
    <template #rightBtn>
      <div @click="showMenu" style="margin: -1rem; padding: 1rem;">
        <img :src="brandAsset(icons.menu, icons.menuSanne)" alt="menu" />
      </div>
    </template>
    <template #content>
      <panel float :height="panelHeight">
        <!-- Tab items -->
        <div class="flex justify-center items-center">
          <span
            class="mx-4 my-6 text-sm"
            :class="[`${isSignUpTab ? 'text-black' : 'text-gray-500'}`]"
            @click="setActiveTab('signup')"
          >
            {{ $t('login.sign_up') }}
          </span>
          <span
            class="mx-4 my-6 text-sm"
            :class="[`${isSignInTab ? 'text-black' : 'text-gray-500'}`]"
            @click="setActiveTab('signin')"
          >
            {{ $t('login.login') }}
          </span>
        </div>
        <!-- Content -->
        <div class="flex flex-col justify-center items-center">
          <!-- Sign up tab -->
          <template v-if="isSignUpTab">
            <c-button class="my-3" icon @click="setAgreementsPage('registered-email')">
              <img slot="icon" src="../assets/common/mail-icon.svg" alt="Email" />
              <span>{{ $t('login.sign_up_email') }}</span>
            </c-button>
            <c-button class="my-3" icon @click="setAgreementsPage('registered-phone')">
              <img slot="icon" src="../assets/common/phone-icon.svg" alt="Phone" />
              <span>{{ $t('login.sign_up_phone') }}</span>
            </c-button>
            <c-button v-if="canCreateWithStore" class="my-3" icon @click="showRegisteredByStore()">
              <img slot="icon" src="../assets/common/store-icon.svg" alt="Phone" />
              <span>{{ $t('login.create_with_store') }}</span>
            </c-button>
          </template>
          <!-- Log in tab -->
          <template v-if="isSignInTab">
            <c-button class="my-3" icon @click="activePage = 'login-email'">
              <img slot="icon" src="../assets/common/mail-icon.svg" alt="Email" />
              <span>{{ $t('login.login_email_title') }}</span>
            </c-button>
            <c-button class="my-3" icon @click="activePage = 'login-phone'">
              <img slot="icon" src="../assets/common/phone-icon.svg" alt="Phone" />
              <span>{{ $t('login.login_phone_title') }}</span>
            </c-button>
          </template>
          <!-- Social auths  -->
          <div
            v-if="appReady && !isAuthenticated"
            class="flex flex-col justify-center items-center"
          >
            <span class="my-3 text-sm">{{ $t('login.login_social_accounts') }}</span>
            <div class="flex justify-center my-3">
              <img
                v-if="isIOS"
                class="mx-6 w-10"
                src="../assets/common/apple-icon.svg"
                alt="Log in with Apple ID"
                @click="signInWithSNS(5)"
              />
              <img
                v-if="showFacebook"
                class="mx-6 w-10"
                src="../assets/common/fb-icon.svg"
                alt="Log in with Facebook"
                @click="signInWithSNS(1)"
              />
              <img
                v-if="showWeChat"
                class="mx-6 w-10"
                src="../assets/common/wechat-icon.svg"
                alt="Log in with WeChat"
                @click="signInWithSNS(3)"
              />
            </div>
            <span class="m-5" v-if="errorMsg">{{ errorMsg }}</span>
          </div>
        </div>
      </panel>
    </template>
    <template v-slot>
      <registered-email
        v-if="activePage === 'registered-email'"
        @done="onLoginDone"
        @clear-active-page="clearActivePage"
      />
      <registered-phone
        v-if="activePage === 'registered-phone'"
        @done="onLoginDone"
        @clear-active-page="clearActivePage"
        @use-email="showEmailRegister"
      />
      <registered-store
        v-if="activePage === 'registered-store'"
        @done="onLoginDone"
        @clear-active-page="clearActivePage"
      />
      <agreements v-if="agreementsPage" :agreements-page.sync="agreementsPage" />
      <login-email
        v-if="activePage === 'login-email'"
        @done="onLoginDone"
        @clear-active-page="clearActivePage"
      />
      <login-phone
        v-if="activePage === 'login-phone'"
        @done="onLoginDone"
        @clear-active-page="clearActivePage"
        @use-email="showEmailLogin"
      />
    </template>
  </page>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import page from '@/mixins/page';
import showAppMenu from '@/mixins/show-app-menu';
import screenWidth from '@/mixins/screen-width';
import brandMode from '@/mixins/brand-mode';
import Page from '@/components/common/Page.vue';
import Panel from '@/components/common/Panel.vue';
import CButton from '@/components/common/CButton.vue';
import Agreements from '@/components/DefaultCloudzet/Login/Agreements.vue';
import RegisteredEmail from '@/components/DefaultCloudzet/Login/RegisteredEmail.vue';
import RegisteredPhone from '@/components/DefaultCloudzet/Login/RegisteredPhone.vue';
import RegisteredStore from '@/components/DefaultCloudzet/Login/RegisteredStore.vue';
import LoginEmail from '@/components/DefaultCloudzet/Login/LoginEmail.vue';
import LoginPhone from '@/components/DefaultCloudzet/Login/LoginPhone.vue';
import ApiActions from '@/modules/api-actions';
import scanIcon from '@/assets/common/scan.svg';
import menuIcon from '@/assets/common/menu.svg';
import scanIconSanne from '@/assets/sanne/icons/scan.svg';
import menuIconSanne from '@/assets/sanne/icons/menu.svg';
import { EVENTS as HW_EVENTS } from '@/events/hardware';

const activeTab = localStorage.getItem('activeTab') || 'signup';

export default {
  name: 'Login',
  mixins: [page, showAppMenu, screenWidth, brandMode],
  components: {
    Page,
    Panel,
    CButton,
    Agreements,
    RegisteredEmail,
    RegisteredPhone,
    RegisteredStore,
    LoginEmail,
    LoginPhone,
  },
  mounted() {
    this.$events.on(HW_EVENTS.SOCIAL_SIGN_IN_DONE, this.onSocialAuthDone);
    this.$events.on(HW_EVENTS.SOCIAL_SIGN_IN_FAILED, this.onSocialAuthFailed);
  },
  beforeDestroy() {
    this.$events.off(HW_EVENTS.SOCIAL_SIGN_IN_DONE, this.onSocialAuthDone);
    this.$events.off(HW_EVENTS.SOCIAL_SIGN_IN_FAILED, this.onSocialAuthFailed);
  },
  data: () => ({
    errorMsg: '',
    message: '',
    active: activeTab,
    activePage: '',
    agreementsPage: false,
    alreadyAgreed: false,
  }),
  methods: {
    ...mapActions('app', ['launchQrScan']),
    setAgreementsPage(data) {
      if (!this.alreadyAgreed) {
        this.agreementsPage = true;
        this.alreadyAgreed = true;
      }
      this.activePage = data;
    },
    setActiveTab(tab) {
      this.active = tab;
      localStorage.setItem('activeTab', tab);
    },
    showRegisteredByStore() {
      this.activePage = 'registered-store';
    },
    signInWithSNS(provider) {
      this.$events.startLoading();
      this.$events.emit('hardware.socialSignIn', provider);
    },
    async onSocialAuthDone(payload) {
      try {
        console.log('onSocialAuthDone', payload);
        await ApiActions.user.loginSNS(payload);
        this.onLoginDone();
      } catch (e) {
        console.log(e.message);
        this.errorMsg = this.$i18n.t('login.social_login_failed');
        this.$events.stopLoading();
      }
    },
    onSocialAuthFailed(event) {
      const { provider, error } = event;
      console.log('onSocialAuthFailed', provider, error);
      this.errorMsg = this.$i18n.t('login.social_login_failed');
      this.$events.stopLoading();
    },
    clearActivePage() {
      this.activePage = '';
    },
    onLoginDone(account) {
      this.$emit('user-login-done', typeof account === 'string' ? account : '');
    },
    showEmailLogin() {
      this.activePage = 'login-email';
    },
    showEmailRegister() {
      this.activePage = 'registered-email';
    },
  },
  computed: {
    ...mapGetters('mtmUser', ['isAuthenticated', 'canCreateWithStore']),
    icons() {
      return { scan: scanIcon, scanSanne: scanIconSanne, menu: menuIcon, menuSanne: menuIconSanne };
    },
    showWeChat() {
      return this.$root.$i18n.locale === 'zh-cn';
    },
    showFacebook() {
      return !this.showWeChat;
    },
    isSignUpTab() {
      return this.active === 'signup';
    },
    isSignInTab() {
      return this.active === 'signin';
    },
    panelHeight() {
      return this.lg ? '50%' : '63%';
    },
  },
};
</script>
